import React, { useState, useMemo, memo } from "react";
import {
  useReactTable,
  getCoreRowModel,
  flexRender,
} from "@tanstack/react-table";
import { Loading, SearchInput, Tooltip } from "./"; // Use common Tooltip component

const DataTable = ({
  columns,
  fetchData,
  currentPage = 1,
  onPageChange,
  onRecordsPerPageChange,
  recordsPerPage = 50,
  searchTerm = "",
  onSearchChange,
  isSearchable = true,
  isPagination = true,
  isPageSize = true,
  isLoading = false,
  handleHeaderSort,
  handleSearchKey,
  data = [],
  pages,
}) => {
  // const { data, pages } = fetchData({
  //   searchTerm,
  //   recordsPerPage,
  //   currentPage,
  // });
  const [sorting, setSorting] = useState({ index: null, direction: null });

  const indexedColumns = useMemo(
    () => [
      {
        id: "index",
        header: "#",
        cell: ({ row }) => row.index + 1 + (currentPage - 1) * recordsPerPage,
      },
      ...columns,
    ],
    [columns, currentPage, recordsPerPage],
  );

  const table = useReactTable({
    columns: indexedColumns,
    data: data || [],
    getCoreRowModel: getCoreRowModel(),
  });

  const handleRecordsPerPageChange = (event) => {
    onRecordsPerPageChange(Number(event.target.value));
    onPageChange(1); // Reset to first page when records per page changes
  };

  const handleSearch = (val) => {
    onSearchChange(val);
    onPageChange(1); // Reset to first page when search term changes
  };

  const renderPageNumbers = () => {
    const visiblePages = 5; // Number of visible page buttons
    const pagesToShow = [];

    let startPage = Math.max(currentPage - Math.floor(visiblePages / 2), 1);
    let endPage = Math.min(startPage + visiblePages - 1, pages);

    if (endPage - startPage + 1 < visiblePages) {
      startPage = Math.max(endPage - visiblePages + 1, 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pagesToShow.push(
        <li className="page-item">
          <button
            key={i}
            className={` ${i === currentPage ? "page-link" : "page-link"}`}
            onClick={() => onPageChange(i)}
          >
            {i}
          </button>
        </li>,
      );
    }

    return pagesToShow;
  };

  const handleHeaderClick = (headerIndex) => {
    setSorting((prevSorting) => {
      const newDirection =
        prevSorting.index === headerIndex && prevSorting.direction === "asc"
          ? "desc"
          : "asc";

      // Only call handleHeaderSort if it's provided
      if (handleHeaderSort) {
        handleHeaderSort({ index: headerIndex, direction: newDirection });
      }

      return { index: headerIndex, direction: newDirection };
    });
  };

  return (
    <>
      {/* Search and Records Per Page Controls */}
      {(isPageSize || isPagination) && (
        <div className="table-toolbar ">
          {isPageSize && (
            <div className="card-title">
              <select
                className="form-select "
                value={recordsPerPage}
                onChange={handleRecordsPerPageChange}
              >
                <option value={50}>50</option>
                <option value={75}>75</option>
                <option value={100}>100</option>
                <option value={200}>200</option>
              </select>
            </div>
          )}

          {isSearchable && (
            <div className="card-title">
              <SearchInput
                onSearchChange={handleSearch}
                handleSearchKey={handleSearchKey}
              />
            </div>
          )}
        </div>
      )}

      {/* Table */}
      {data ? (
        <div className="table-responsive">
          <table className="report-card-box">
            <thead>
              {table.getHeaderGroups().map((headerGroup) => (
                <tr key={headerGroup.id}>
                  {headerGroup.headers.map((header, index) => (
                    <th key={index} onClick={() => handleHeaderClick(index)}>
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext(),
                          )}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan={indexedColumns.length} className="text-center">
                    <Loading />
                  </td>
                </tr>
              ) : data && data.length === 0 ? (
                <tr>
                  <td colSpan={indexedColumns.length} className="text-center">
                    <div className="not_rows">
                      <svg
                        width="24"
                        height="24"
                        viewBox="0 0 64 64"
                        fill="currentColor"
                        xmlns="http://www.w3.org/2000/svg"
                        cursor="pointer"
                        className="not_icon"
                      >
                        <path
                          fill="var(--main-color)"
                          d="M42.399 34.23a.5.5 0 0 0-.949-.239c-1.663 3.413-4.983 6.246-7.516 7.513a.5.5 0 0 0 .244.947c1.165-.046 2.7.033 4.497.452a.5.5 0 0 0 .6-.372c.516-2.181 2.009-2.935 3.199-3.203a.5.5 0 0 0 .376-.601 17 17 0 0 1-.451-4.497"
                          data-original="var(--main-color)"
                        ></path>
                        <path
                          fill="var(--blue-color)"
                          d="M18.432 15.195c-.914.431-2.654 1.957-3.609 3.61-.928 1.607-1.417 3.457-1.419 4.717a.5.5 0 0 0 1 .002c.002-1.129.454-2.78 1.285-4.219.843-1.46 2.362-2.825 3.169-3.205a.5.5 0 1 0-.426-.905"
                          data-original="var(--blue-color)"
                        ></path>
                        <path
                          fill="var(--blue-color)"
                          d="M14.925 14.977c5.854-5.854 15.359-5.854 21.213 0s5.854 15.359 0 21.213-15.359 5.854-21.213 0c-4.799-4.799-5.664-12.051-2.595-17.735a.5.5 0 0 0-.88-.475c-3.274 6.062-2.351 13.798 2.768 18.917 6.244 6.244 16.383 6.244 22.627 0s6.244-16.383 0-22.627-16.383-6.244-22.627 0a.5.5 0 0 0 .707.707"
                          data-original="var(--blue-color)"
                        ></path>
                        <path
                          fill="var(--blue-color)"
                          d="M42.956 30.1a17.9 17.9 0 0 1-4.697 8.211c-7.024 7.025-18.431 7.025-25.455 0-7.025-7.024-7.025-18.431 0-25.455 7.024-7.025 18.431-7.025 25.455 0a17.95 17.95 0 0 1 5.263 13.2.5.5 0 1 0 .999.026 18.95 18.95 0 0 0-5.555-13.934c-7.415-7.415-19.455-7.415-26.87 0s-7.415 19.456 0 26.871 19.455 7.415 26.87 0a18.9 18.9 0 0 0 4.958-8.669.5.5 0 1 0-.968-.25m17.496 24.747a3 3 0 0 1-.203.524c-.387.724-1.179 1.728-2.191 2.739-1.011 1.012-2.015 1.804-2.739 2.191a3 3 0 0 1-.524.203L42.91 48.619a.5.5 0 0 0-.707.707l11.966 11.966c.21.211.638.313 1.22.081.794-.318 2.086-1.265 3.376-2.555s2.238-2.583 2.555-3.377c.233-.582.13-1.01-.08-1.22L46.745 39.726a5 5 0 0 0-7.071 0s-.143.143 0 0a5 5 0 0 0 0 7.071.5.5 0 0 0 .707-.707 4 4 0 0 1 0-5.657c-.094.094 0 0 0 0a4 4 0 0 1 5.657 0z"
                          data-original="var(--blue-color)"
                        ></path>
                        <path
                          d="M23.193 22.435c-.023-.953.334-1.649.887-2.057.868-.64 2.164-.613 2.868.18.303.341.478.823.483 1.433.012 1.463-.674 2.318-1.346 3.203-.904 1.193-1.807 2.419-1.919 4.466a1 1 0 1 0 1.997.109c.085-1.555.829-2.461 1.516-3.367.92-1.213 1.768-2.423 1.752-4.427-.01-1.178-.403-2.087-.987-2.745-1.365-1.536-3.87-1.702-5.551-.462-1.008.744-1.741 1.976-1.7 3.714a1 1 0 0 0 2-.047"
                          data-original="var(--main-color)"
                        ></path>
                        <path
                          d="m43.288 8.497 3-5a1 1 0 1 0-1.715-1.029l-3 5a1 1 0 0 0 1.715 1.029m4.731 3.295 11-8a1 1 0 0 0-1.176-1.618l-11 8a1 1 0 0 0 1.176 1.618m1.654 6.161 8-2a1 1 0 1 0-.485-1.94l-8 2a1 1 0 0 0 .485 1.94"
                          data-original="var(--main-color)"
                        ></path>
                        <circle
                          cx="25"
                          cy="33"
                          r="1"
                          fill="currentColor"
                        ></circle>
                      </svg>
                      No records found
                    </div>
                  </td>
                </tr>
              ) : (
                table.getRowModel().rows.map((row, index) => (
                  <tr key={index}>
                    {row.getVisibleCells().map((cell, index1) => (
                      <td key={index1}>
                        {/* <Tooltip
                          content={cell.column.columnDef.tooltip}
                          position="top"
                        > */}
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext(),
                        )}
                        {/* </Tooltip> */}
                      </td>
                    ))}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="not_rows">
          <svg
            width="24"
            height="24"
            viewBox="0 0 64 64"
            fill="currentColor"
            xmlns="http://www.w3.org/2000/svg"
            cursor="pointer"
            className="not_icon"
          >
            <path
              fill="var(--main-color)"
              d="M42.399 34.23a.5.5 0 0 0-.949-.239c-1.663 3.413-4.983 6.246-7.516 7.513a.5.5 0 0 0 .244.947c1.165-.046 2.7.033 4.497.452a.5.5 0 0 0 .6-.372c.516-2.181 2.009-2.935 3.199-3.203a.5.5 0 0 0 .376-.601 17 17 0 0 1-.451-4.497"
              data-original="var(--main-color)"
            ></path>
            <path
              fill="var(--blue-color)"
              d="M18.432 15.195c-.914.431-2.654 1.957-3.609 3.61-.928 1.607-1.417 3.457-1.419 4.717a.5.5 0 0 0 1 .002c.002-1.129.454-2.78 1.285-4.219.843-1.46 2.362-2.825 3.169-3.205a.5.5 0 1 0-.426-.905"
              data-original="var(--blue-color)"
            ></path>
            <path
              fill="var(--blue-color)"
              d="M14.925 14.977c5.854-5.854 15.359-5.854 21.213 0s5.854 15.359 0 21.213-15.359 5.854-21.213 0c-4.799-4.799-5.664-12.051-2.595-17.735a.5.5 0 0 0-.88-.475c-3.274 6.062-2.351 13.798 2.768 18.917 6.244 6.244 16.383 6.244 22.627 0s6.244-16.383 0-22.627-16.383-6.244-22.627 0a.5.5 0 0 0 .707.707"
              data-original="var(--blue-color)"
            ></path>
            <path
              fill="var(--blue-color)"
              d="M42.956 30.1a17.9 17.9 0 0 1-4.697 8.211c-7.024 7.025-18.431 7.025-25.455 0-7.025-7.024-7.025-18.431 0-25.455 7.024-7.025 18.431-7.025 25.455 0a17.95 17.95 0 0 1 5.263 13.2.5.5 0 1 0 .999.026 18.95 18.95 0 0 0-5.555-13.934c-7.415-7.415-19.455-7.415-26.87 0s-7.415 19.456 0 26.871 19.455 7.415 26.87 0a18.9 18.9 0 0 0 4.958-8.669.5.5 0 1 0-.968-.25m17.496 24.747a3 3 0 0 1-.203.524c-.387.724-1.179 1.728-2.191 2.739-1.011 1.012-2.015 1.804-2.739 2.191a3 3 0 0 1-.524.203L42.91 48.619a.5.5 0 0 0-.707.707l11.966 11.966c.21.211.638.313 1.22.081.794-.318 2.086-1.265 3.376-2.555s2.238-2.583 2.555-3.377c.233-.582.13-1.01-.08-1.22L46.745 39.726a5 5 0 0 0-7.071 0s-.143.143 0 0a5 5 0 0 0 0 7.071.5.5 0 0 0 .707-.707 4 4 0 0 1 0-5.657c-.094.094 0 0 0 0a4 4 0 0 1 5.657 0z"
              data-original="var(--blue-color)"
            ></path>
            <path
              d="M23.193 22.435c-.023-.953.334-1.649.887-2.057.868-.64 2.164-.613 2.868.18.303.341.478.823.483 1.433.012 1.463-.674 2.318-1.346 3.203-.904 1.193-1.807 2.419-1.919 4.466a1 1 0 1 0 1.997.109c.085-1.555.829-2.461 1.516-3.367.92-1.213 1.768-2.423 1.752-4.427-.01-1.178-.403-2.087-.987-2.745-1.365-1.536-3.87-1.702-5.551-.462-1.008.744-1.741 1.976-1.7 3.714a1 1 0 0 0 2-.047"
              data-original="var(--main-color)"
            ></path>
            <path
              d="m43.288 8.497 3-5a1 1 0 1 0-1.715-1.029l-3 5a1 1 0 0 0 1.715 1.029m4.731 3.295 11-8a1 1 0 0 0-1.176-1.618l-11 8a1 1 0 0 0 1.176 1.618m1.654 6.161 8-2a1 1 0 1 0-.485-1.94l-8 2a1 1 0 0 0 .485 1.94"
              data-original="var(--main-color)"
            ></path>
            <circle cx="25" cy="33" r="1" fill="currentColor"></circle>
          </svg>
          No Record
        </div>
      )}

      {/* Pagination Controls */}
      <div className="page-table">
        <div className="page-table__info">
          <span className="dt-total">{data.length} Rows</span>
        </div>
        {isPagination ? (
          !isLoading ? (
            <div className="page-table__pagination">
              <ul className="pagination">
                <li className="page-item">
                  <button
                    className="page-link"
                    onClick={() => onPageChange(1)}
                    disabled={currentPage === 1}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="15"
                      height="15"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      className="lucide lucide-chevrons-left"
                      aria-hidden="true"
                    >
                      <path d="m11 17-5-5 5-5"></path>
                      <path d="m18 17-5-5 5-5"></path>
                    </svg>
                  </button>
                </li>

                <li className="page-item">
                  <button
                    className="page-link "
                    onClick={() => onPageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="15"
                      height="15"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      className="lucide lucide-chevron-left"
                      aria-hidden="true"
                    >
                      <path d="m15 18-6-6 6-6"></path>
                    </svg>
                  </button>
                </li>

                {/* Page Numbers */}
                {renderPageNumbers()}

                {/* Next and Last */}

                <li className="page-item">
                  <button
                    className="page-link "
                    onClick={() => onPageChange(currentPage + 1)}
                    disabled={currentPage === pages}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="15"
                      height="15"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      className="lucide lucide-chevron-right"
                      aria-hidden="true"
                    >
                      <path d="m9 18 6-6-6-6"></path>
                    </svg>
                  </button>
                </li>

                <li className="page-item">
                  <button
                    className="page-link"
                    onClick={() => onPageChange(pages)}
                    disabled={currentPage === pages}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="15"
                      height="15"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      className="lucide lucide-chevrons-right"
                      aria-hidden="true"
                    >
                      <path d="m6 17 5-5-5-5"></path>
                      <path d="m13 17 5-5-5-5"></path>
                    </svg>
                  </button>
                </li>
              </ul>
            </div>
          ) : (
            ""
          )
        ) : (
          ""
        )}
      </div>
    </>
  );
};

export default DataTable;
